import { MAIL_HOSTING } from '../../constants/mail_hosting';
import { defineFeature } from '../define-feature';
import { TEST_IDS, sidebarNavTestId, sidebarSubTestId } from '../test-ids';

export default defineFeature({
  id: 'website.email.configure',
  category: 'website',
  title: 'Configurer une adresse email professionnelle',
  intent:
    "Créer une adresse email professionnelle sur votre nom de domaine (par exemple contact@mon-salon.fr) et la brancher sur votre application mail habituelle — Gmail, Apple Mail, Outlook ou autre. Propelsia gère automatiquement le domaine et les enregistrements DNS ; il ne vous reste qu'à ajouter le compte dans votre client mail avec les réglages fournis ci-dessous.",
  context: {
    why: "Avoir une adresse email sur votre propre nom de domaine (comme contact@mon-salon.fr plutôt que monsalon@gmail.com) change radicalement l'image professionnelle que vous renvoyez à vos clients et à vos partenaires. C'est un petit détail qui a un grand impact : une adresse en @gmail.com ou @hotmail.com donne l'impression d'une activité amateur, tandis qu'une adresse sur votre propre domaine transmet immédiatement le message d'un business structuré et sérieux. Cette adresse peut aussi devenir un canal marketing en soi — vous pouvez créer plusieurs adresses spécialisées (contact@, reservation@, sav@) pour bien orienter vos communications entrantes, et vos clients savent tout de suite à qui ils s'adressent. Propelsia rend cette configuration accessible même sans compétences techniques : le domaine et les enregistrements DNS sont configurés automatiquement en arrière-plan. Vous n'avez qu'à créer la boîte, copier le mot de passe généré, et ajouter le compte dans votre application mail habituelle. Tout ce qui est complexe est pris en charge sans que vous n'ayez à toucher à un panneau de configuration DNS ou à comprendre les subtilités de la messagerie électronique.",
    use_cases: [
      'Vous venez de lancer votre entreprise et vous voulez immédiatement une adresse pro : créez votre première boîte mail sur votre domaine dès la mise en ligne du site.',
      'Vous voulez plusieurs adresses spécialisées (contact@, reservation@, sav@, comptabilite@) : créez-en autant que nécessaire, chacune avec son propre mot de passe.',
      "Vous embauchez un nouveau membre dans l'équipe : créez-lui son adresse personnelle sur le domaine de l'entreprise.",
      'Vous voulez que vos e-mails partent depuis votre adresse pro tout en utilisant votre client mail habituel (Gmail, Apple Mail, Outlook) : la configuration IMAP/SMTP permet cette flexibilité.',
      'Vous préparez une campagne marketing depuis une adresse spécifique pour tracer les retours : créez une adresse dédiée.',
    ],
  },
  required_scopes: ['establishment.email_pro'],
  ui_entry_points: [
    {
      route: '/website/domain',
      element:
        'Site internet, onglet « Domaine et emails », section « Adresses email » — bouton « Créer une boîte mail »',
    },
  ],
  api: {
    primary_operation_id: 'EmailHostingAppController_createMailbox',
    related_operation_ids: ['EmailHostingAppController_listMailboxes'],
  },
  steps: [
    "Ouvrir Site internet, onglet « Domaine et emails ». La création de boîtes n'est possible que sur un domaine que vous possédez (acheté ou raccordé).",
    "Cliquer sur « Créer une boîte mail », choisir le domaine, saisir le nom de l'adresse (la partie avant le @) et, si besoin, un nom d'affichage.",
    "Valider : le mot de passe s'affiche une seule fois — le copier immédiatement et le conserver dans un gestionnaire de mots de passe.",
    'Ouvrir votre application mail et ajouter un compte de type « Autre » / « IMAP » avec les réglages de connexion ci-dessous.',
    "Utiliser l'adresse email complète comme identifiant et le mot de passe généré comme mot de passe.",
  ],
  gotchas: [
    `Serveur entrant (IMAP) : \`${MAIL_HOSTING.IMAP_HOST}\`, port \`${MAIL_HOSTING.IMAP_PORT}\`, chiffrement SSL/TLS.`,
    `Serveur sortant (SMTP) : \`${MAIL_HOSTING.SMTP_HOST}\`, port \`${MAIL_HOSTING.SMTP_PORT}\`, chiffrement SSL/TLS.`,
    "L'identifiant de connexion est toujours l'adresse email **complète** (ex : `contact@mon-salon.fr`), jamais seulement la partie avant le @.",
    "Le mot de passe n'est affiché qu'une seule fois à la création. Si vous l'avez perdu, utilisez « Régénérer le mot de passe » sur la boîte concernée — l'ancien mot de passe cesse alors de fonctionner.",
    'Une boîte fraîchement créée peut mettre quelques minutes à devenir active, le temps que la configuration DNS se propage.',
    "Le nombre de boîtes incluses dépend de votre formule d'abonnement.",
  ],
  related_features: ['establishment.info.update'],
  demo_script: {
    // Écran gated (accès bêta + formule + nom de domaine connecté) : démo sobre qui
    // montre le point d'entrée « Créer une adresse ». Création complète = flux dépendant
    // d'un domaine actif, à réactiver quand testable en seed.
    duration_target_s: 20,
    steps: [
      {
        action: 'navigate',
        value: '/fr-FR',
        caption: 'Une adresse e-mail à votre nom de domaine, c’est un vrai gage de sérieux face à vos clients.',
      },
      { action: 'click', target: sidebarNavTestId('/website'), caption: 'Menu latéral, Site internet.' },
      {
        action: 'click',
        target: sidebarSubTestId('/website/domain'),
        caption: 'Onglet Nom de domaine, qui héberge aussi vos adresses e-mail.',
      },
      {
        action: 'navigate',
        value: '/fr-FR/website/domain?email_beta=1',
        caption: 'La section des adresses e-mail professionnelles s’ouvre.',
      },
      {
        action: 'expect_visible',
        target: TEST_IDS.mailbox_create,
        caption: 'Le bouton « Créer une adresse » lance la création de votre e-mail pro.',
        capture: true,
      },
    ],
  },
});
